import {
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IFloatingFilterAngularComp } from 'ag-grid-angular';
import { IFloatingFilterParams } from 'ag-grid-community';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import {
  AgNumberFilterComponent,
  NumberFilterModel,
} from './ag-number-filter.component';

const TYPING_DEBOUNCE_MS = 300;
const READ_ONLY_TYPES = new Set<NumberFilterModel['type']>([
  'blank',
  'notBlank',
  'inRange',
]);

/**
 * The header-row box of AgNumberFilterComponent (WAP-1647): a typed number
 * applies with the popup's current single-value operator (equals otherwise),
 * an empty box clears the filter, popup edits flow back. In range and Blank /
 * Not blank show their summary read-only; clicking it opens the popup.
 */
@Component({
  selector: 'app-ag-number-floating-filter',
  templateUrl: './ag-number-floating-filter.component.html',
  styleUrls: ['./ag-number-floating-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AgNumberFloatingFilterComponent implements IFloatingFilterAngularComp {
  private params!: IFloatingFilterParams<AgNumberFilterComponent>;
  private readonly typed$ = new Subject<number | null>();
  private readonly destroyRef = inject(DestroyRef);

  value: number | null = null;
  summary = '';
  readonly = false;

  agInit(params: IFloatingFilterParams<AgNumberFilterComponent>): void {
    this.params = params;
    this.typed$
      .pipe(
        debounceTime(TYPING_DEBOUNCE_MS),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((value) =>
        params.parentFilterInstance((instance) =>
          instance.applyFromFloating(value),
        ),
      );
  }

  onParentModelChanged(model: NumberFilterModel | null): void {
    this.readonly = model != null && READ_ONLY_TYPES.has(model.type);
    this.value = model && !this.readonly ? (model.filter ?? null) : null;
    this.summary = this.readonly ? this.summaryOf() : '';
  }

  onInput(value: number | string | null): void {
    const n = value === '' || value == null ? null : Number(value);
    this.value = n != null && Number.isFinite(n) ? n : null;
    this.typed$.next(this.value);
  }

  openPopup(): void {
    this.params.showParentFilter();
  }

  private summaryOf(): string {
    let summary = '';
    this.params.parentFilterInstance((instance) => {
      summary = instance.getModelAsString();
    });
    return summary;
  }
}
